import { useEffect, useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import type { ColumnDef } from '@tanstack/react-table';
import {
  Bell,
  Loader2,
  MessageSquare,
  Plus,
  Send,
  Smartphone,
  Trash2,
  LayoutTemplate,
  Code,
} from 'lucide-react';
import { TopBanner } from '@/components/TopBanner';
import { DataTable } from '@/components/DataTable';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  usePushSubscriptions,
  useNotificationHistory,
  usePopupTemplates,
  useCreateNotification,
  useSendNotification,
  useCreatePopupTemplate,
  useRemovePopupTemplate,
  useSendPopup,
  useRemoveSubscription,
} from '@/hooks/useNotifications';
import type { PushSubscription, NotificationItem, PopupTemplate } from '@/api/notifications.api';
import type { PushAutoConfig, PushAutoRule } from '@/api/push-auto.api';
import { usePushStats, useSendPush } from '@/hooks/usePushCampaign';
import { usePushAutoConfig, usePushAutoStats, useSavePushAuto } from '@/hooks/usePushAuto';
import { formatDate } from '@/lib/utils';

export const Route = createFileRoute('/_authenticated/notificaciones')({
  component: NotificacionesPage,
});

const TABS = [
  { key: 'push', label: 'Web Push', icon: Bell },
  { key: 'history', label: 'Historial', icon: MessageSquare },
  { key: 'templates', label: 'Templates', icon: LayoutTemplate },
  { key: 'push-auto', label: 'Push Auto', icon: Smartphone },
] as const;

const createNotificationSchema = z.object({
  kind: z.enum(['push', 'popup', 'email', 'wa']),
  title: z.string().min(1, 'Requerido').max(160),
  body: z.string().min(1, 'Requerido').max(2000),
  mediaUrl: z.string().url().optional().or(z.literal('')),
  ctaUrl: z.string().url().optional().or(z.literal('')),
  scheduledAt: z.string().optional(),
});
type CreateNotificationForm = z.infer<typeof createNotificationSchema>;

const createTemplateSchema = z.object({
  name: z.string().min(1, 'Requerido').max(120),
  htmlTemplate: z.string().min(1, 'Requerido').max(10000),
});
type CreateTemplateForm = z.infer<typeof createTemplateSchema>;

function NotificacionesPage() {
  const [activeTab, setActiveTab] = useState<(typeof TABS)[number]['key']>('push');

  return (
    <>
      <TopBanner title="Notificaciones" subtitle="Web Push + popups + templates" />
      <div className="flex-1 overflow-auto p-6">
        <PushComposer />

        <div className="mb-4 flex gap-2">
          {TABS.map((t) => (
            <Button
              key={t.key}
              variant={activeTab === t.key ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveTab(t.key)}
            >
              <t.icon className="mr-2 h-4 w-4" />
              {t.label}
            </Button>
          ))}
        </div>

        {activeTab === 'push' && <PushTab />}
        {activeTab === 'history' && <HistoryTab />}
        {activeTab === 'templates' && <TemplatesTab />}
        {activeTab === 'push-auto' && <PushAutoTab />}
      </div>
    </>
  );
}

// ========================================================================
// Composer: Enviar Push ahora (entrega REAL vía PushService)
// ========================================================================

function PushComposer() {
  const { data: stats } = usePushStats();
  const sendMutation = useSendPush();

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [url, setUrl] = useState('');
  const [target, setTarget] = useState<'all' | 'client'>('all');
  const [clientId, setClientId] = useState('');

  const canSend =
    body.trim().length > 0 && (target === 'all' || clientId.trim().length > 0);

  const onSend = () => {
    if (!canSend) return;
    sendMutation.mutate(
      {
        title: title.trim() || undefined,
        body: body.trim(),
        url: url.trim() || undefined,
        target,
        clientId: target === 'client' ? clientId.trim() : undefined,
      },
      {
        onSuccess: () => {
          setTitle('');
          setBody('');
          setUrl('');
        },
      },
    );
  };

  return (
    <Card className="mb-6">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <Send className="h-5 w-5" />
          Enviar Push
        </CardTitle>
        <Badge variant="secondary">
          {stats?.subscriptions ?? 0} dispositivo(s) suscripto(s)
        </Badge>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <label className="text-xs font-medium text-muted-foreground">Título</label>
          <Input
            placeholder="Casino — ¡Bono especial!"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>
        <div>
          <label className="text-xs font-medium text-muted-foreground">Mensaje</label>
          <textarea
            className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            placeholder="Tenés un bono del 100% esperándote…"
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="text-xs font-medium text-muted-foreground">URL al hacer click (opcional)</label>
            <Input placeholder="/widget" value={url} onChange={(e) => setUrl(e.target.value)} />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Destinatario</label>
            <Select value={target} onValueChange={(v) => setTarget(v as 'all' | 'client')}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="client">Un cliente</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        {target === 'client' && (
          <div>
            <label className="text-xs font-medium text-muted-foreground">Usuario o ID del cliente</label>
            <Input
              placeholder="usuario_casino"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
            />
          </div>
        )}
        <div className="flex justify-end">
          <Button onClick={onSend} disabled={!canSend || sendMutation.isPending}>
            {sendMutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Send className="mr-2 h-4 w-4" />
            )}
            Enviar Notificación
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ========================================================================
// Push Subscriptions Tab
// ========================================================================

function PushTab() {
  const { data, isLoading } = usePushSubscriptions();
  const removeMutation = useRemoveSubscription();

  const columns = useMemo<ColumnDef<PushSubscription>[]>(
    () => [
      {
        accessorKey: 'endpoint',
        header: 'Endpoint',
        cell: ({ row }) => (
          <span className="font-mono text-xs max-w-[300px] truncate block">
            {row.original.endpoint}
          </span>
        ),
      },
      {
        accessorKey: 'userAgent',
        header: 'User Agent',
        cell: ({ row }) => row.original.userAgent ?? '—',
      },
      {
        accessorKey: 'createdAt',
        header: 'Suscrito',
        cell: ({ row }) => formatDate(row.original.createdAt),
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row }) => (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => removeMutation.mutate(row.original.id)}
            disabled={removeMutation.isPending}
          >
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        ),
      },
    ],
    [removeMutation],
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="h-5 w-5" />
          Suscripciones Push
        </CardTitle>
      </CardHeader>
      <CardContent>
        <DataTable
          columns={columns}
          data={data ?? []}
          isLoading={isLoading}
          emptyMessage="No hay suscripciones push"
        />
      </CardContent>
    </Card>
  );
}

// ========================================================================
// History Tab
// ========================================================================

function HistoryTab() {
  const { data, isLoading } = useNotificationHistory();
  const sendMutation = useSendNotification();
  const [openCreate, setOpenCreate] = useState(false);

  const columns = useMemo<ColumnDef<NotificationItem>[]>(
    () => [
      {
        accessorKey: 'kind',
        header: 'Tipo',
        cell: ({ row }) => (
          <Badge variant={row.original.kind === 'push' ? 'default' : 'secondary'}>
            {row.original.kind.toUpperCase()}
          </Badge>
        ),
      },
      {
        accessorKey: 'title',
        header: 'Título',
        cell: ({ row }) => row.original.title ?? '—',
      },
      {
        accessorKey: 'body',
        header: 'Mensaje',
        cell: ({ row }) => (
          <span className="max-w-[300px] truncate block">{row.original.body ?? '—'}</span>
        ),
      },
      {
        accessorKey: 'scheduledAt',
        header: 'Programado',
        cell: ({ row }) => formatDate(row.original.scheduledAt),
      },
      {
        accessorKey: 'sentAt',
        header: 'Enviado',
        cell: ({ row }) =>
          row.original.sentAt ? (
            <Badge variant="success">Enviado</Badge>
          ) : (
            <Badge variant="warning">Pendiente</Badge>
          ),
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row }) =>
          !row.original.sentAt && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => sendMutation.mutate(row.original.id)}
              disabled={sendMutation.isPending}
            >
              <Send className="mr-2 h-4 w-4" />
              Enviar
            </Button>
          ),
      },
    ],
    [sendMutation],
  );

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <MessageSquare className="h-5 w-5" />
          Historial
        </CardTitle>
        <Button size="sm" onClick={() => setOpenCreate(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Nueva
        </Button>
      </CardHeader>
      <CardContent>
        <DataTable
          columns={columns}
          data={data ?? []}
          isLoading={isLoading}
          emptyMessage="No hay notificaciones"
        />
      </CardContent>

      <NewNotificationDialog open={openCreate} onOpenChange={setOpenCreate} />
    </Card>
  );
}

function NewNotificationDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const createMutation = useCreateNotification();
  const form = useForm<CreateNotificationForm>({
    resolver: zodResolver(createNotificationSchema),
    defaultValues: {
      kind: 'push',
      title: '',
      body: '',
      mediaUrl: '',
      ctaUrl: '',
      scheduledAt: '',
    },
  });

  const onSubmit = async (values: CreateNotificationForm) => {
    await createMutation.mutateAsync({
      kind: values.kind,
      title: values.title,
      body: values.body,
      mediaUrl: values.mediaUrl || undefined,
      ctaUrl: values.ctaUrl || undefined,
      scheduledAt: values.scheduledAt || undefined,
      audience: { all: true },
    });
    form.reset();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Nueva notificación</DialogTitle>
          <DialogDescription>Creá una notificación push o popup para tus clientes.</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="kind"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tipo</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="push">Web Push</SelectItem>
                      <SelectItem value="popup">Popup</SelectItem>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="wa">WhatsApp</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Título</FormLabel>
                  <FormControl>
                    <Input placeholder="Título de la notificación" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="body"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Mensaje</FormLabel>
                  <FormControl>
                    <textarea
                      className="flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      placeholder="Cuerpo del mensaje"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="mediaUrl"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>URL de imagen (opcional)</FormLabel>
                  <FormControl>
                    <Input placeholder="https://..." {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="ctaUrl"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>URL del botón (opcional)</FormLabel>
                  <FormControl>
                    <Input placeholder="https://..." {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="scheduledAt"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Programar (opcional)</FormLabel>
                  <FormControl>
                    <Input type="datetime-local" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Crear
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

// ========================================================================
// Templates Tab
// ========================================================================

function TemplatesTab() {
  const { data, isLoading } = usePopupTemplates();
  const removeMutation = useRemovePopupTemplate();
  const sendMutation = useSendPopup();
  const [openCreate, setOpenCreate] = useState(false);

  const columns = useMemo<ColumnDef<PopupTemplate>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Nombre',
        cell: ({ row }) => (
          <span className="flex items-center gap-2 font-medium">
            <LayoutTemplate className="h-4 w-4 text-muted-foreground" />
            {row.original.name}
          </span>
        ),
      },
      {
        accessorKey: 'htmlTemplate',
        header: 'HTML',
        cell: ({ row }) => (
          <span className="flex items-center gap-1 font-mono text-xs text-muted-foreground max-w-[300px] truncate">
            <Code className="h-3 w-3" />
            {row.original.htmlTemplate ?? '—'}
          </span>
        ),
      },
      {
        accessorKey: 'createdAt',
        header: 'Creado',
        cell: ({ row }) => formatDate(row.original.createdAt),
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex justify-end gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => sendMutation.mutate({ templateId: row.original.id, audience: { all: true } })}
              disabled={sendMutation.isPending}
            >
              <Send className="mr-2 h-4 w-4" />
              Enviar
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => removeMutation.mutate(row.original.id)}
              disabled={removeMutation.isPending}
            >
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          </div>
        ),
      },
    ],
    [removeMutation, sendMutation],
  );

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <LayoutTemplate className="h-5 w-5" />
          Templates de popup
        </CardTitle>
        <Button size="sm" onClick={() => setOpenCreate(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Nuevo
        </Button>
      </CardHeader>
      <CardContent>
        <DataTable
          columns={columns}
          data={data ?? []}
          isLoading={isLoading}
          emptyMessage="No hay templates"
        />
      </CardContent>

      <NewTemplateDialog open={openCreate} onOpenChange={setOpenCreate} />
    </Card>
  );
}

function NewTemplateDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const createMutation = useCreatePopupTemplate();
  const form = useForm<CreateTemplateForm>({
    resolver: zodResolver(createTemplateSchema),
    defaultValues: {
      name: '',
      htmlTemplate: '',
    },
  });

  const onSubmit = async (values: CreateTemplateForm) => {
    await createMutation.mutateAsync({
      name: values.name,
      htmlTemplate: values.htmlTemplate,
    });
    form.reset();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Nuevo template de popup</DialogTitle>
          <DialogDescription>Definí el HTML del popup. Podés usar variables.</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nombre</FormLabel>
                  <FormControl>
                    <Input placeholder="Promo bienvenida" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="htmlTemplate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>HTML</FormLabel>
                  <FormControl>
                    <textarea
                      className="flex min-h-[160px] w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      placeholder="<div>Hola...</div>"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Crear
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

// ========================================================================
// Push Auto Tab (reglas de reenganche por inactividad)
// ========================================================================

const emptyPushAutoConfig: PushAutoConfig = { enabled: false, rules: [] };

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-md border p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-xl font-semibold">{value}</p>
    </div>
  );
}

function PushAutoTab() {
  const { data: stats } = usePushAutoStats();
  const { data: config, isLoading } = usePushAutoConfig();
  const saveMutation = useSavePushAuto();
  const [cfg, setCfg] = useState<PushAutoConfig>(emptyPushAutoConfig);

  useEffect(() => {
    if (config) setCfg(config);
  }, [config]);

  const updateRule = (index: number, patch: Partial<PushAutoRule>) => {
    setCfg((prev) => ({
      ...prev,
      rules: prev.rules.map((r, i) => (i === index ? { ...r, ...patch } : r)),
    }));
  };

  const removeRule = (index: number) => {
    setCfg((prev) => ({ ...prev, rules: prev.rules.filter((_, i) => i !== index) }));
  };

  const addRule = () => {
    setCfg((prev) => ({
      ...prev,
      rules: [...prev.rules, { inactiveDays: 7, title: '', body: '', url: '', enabled: true }],
    }));
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Smartphone className="h-5 w-5" />
          Push Auto
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {stats && (
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <StatCard label="Total Clientes" value={stats.totalClients} />
            <StatCard label="Con Push" value={stats.withPush} />
            <StatCard label="Adopción" value={`${stats.adoptionPct}%`} />
            <StatCard label="Enviados hoy" value={stats.sentToday} />
          </div>
        )}

        <label className="flex items-center gap-2 rounded-md border p-3">
          <input
            type="checkbox"
            className="h-4 w-4"
            checked={cfg.enabled}
            onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })}
          />
          <span className="text-sm font-medium">
            Sistema {cfg.enabled ? 'ACTIVADO' : 'DESACTIVADO'}
          </span>
        </label>

        <div className="space-y-3">
          {cfg.rules.map((r, i) => (
            <div key={i} className="rounded-md border p-3 space-y-2">
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Días inactivo</label>
                  <Input
                    type="number"
                    value={r.inactiveDays}
                    onChange={(e) => updateRule(i, { inactiveDays: Number(e.target.value) })}
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Título</label>
                  <Input
                    value={r.title}
                    onChange={(e) => updateRule(i, { title: e.target.value })}
                    placeholder="Título"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Mensaje</label>
                <Input
                  value={r.body}
                  onChange={(e) => updateRule(i, { body: e.target.value })}
                  placeholder="Mensaje"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">URL (opcional)</label>
                <Input
                  value={r.url ?? ''}
                  onChange={(e) => updateRule(i, { url: e.target.value })}
                  placeholder="/widget"
                />
              </div>
              <div className="flex justify-end">
                <Button variant="ghost" size="sm" onClick={() => removeRule(i)}>
                  <Trash2 className="mr-2 h-4 w-4 text-destructive" />
                  Quitar
                </Button>
              </div>
            </div>
          ))}
        </div>

        <div className="flex justify-between">
          <Button variant="outline" size="sm" onClick={addRule}>
            <Plus className="mr-2 h-4 w-4" />
            Nueva regla
          </Button>
          <Button onClick={() => saveMutation.mutate(cfg)} disabled={saveMutation.isPending || isLoading}>
            {saveMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Guardar Configuración
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
